import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Circle,
  Cpu,
  Loader2,
  Sun,
  BatteryMedium,
  Home,
  RadioTower,
  Monitor,
  Info,
  Download,
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import TopNav from "@/components/TopNav";
import PeriodPicker from "@/components/PeriodPicker";
import { getDevice, getDeviceEnergy, getDeviceProfile, getDeviceHistory } from "@/lib/api";
import { kyivDayStartMs, kyivTicks, fmtKyivHM } from "@/lib/time";

const online = (iso) => iso && Date.now() - new Date(iso).getTime() < 60000;
const n = (x) => (typeof x === "number" ? x : 0);

function Panel({ title, right, children, className = "", testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border border-slate-200 bg-white shadow-sm ${className}`}
    >
      {title && (
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <span className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">
            {title}
          </span>
          {right}
        </div>
      )}
      <div className="p-5">{children}</div>
    </div>
  );
}

const fmtPower = (w) => {
  const a = Math.abs(w);
  return a >= 1000
    ? { num: (w / 1000).toFixed(2), unit: "kW" }
    : { num: `${w}`, unit: "W" };
};

function Val({ w }) {
  const { num, unit } = fmtPower(w);
  return (
    <span className="font-mono text-sm font-bold text-slate-900">
      {num}
      <span className="ml-0.5 text-[10px] font-medium text-slate-400">{unit}</span>
    </span>
  );
}

function FGNode({
  x,
  y,
  icon: Icon,
  iconClass,
  boxClass = "border-slate-200",
  label,
  w,
  top,
  topClass,
  bottom,
  bottomClass,
}) {
  return (
    <div
      className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center"
      style={{ left: `${(x / 1000) * 100}%`, top: `${(y / 900) * 100}%` }}
    >
      {top && <div className={`mb-0.5 text-xs font-bold ${topClass}`}>{top}</div>}
      <div
        className={`flex h-12 w-12 items-center justify-center rounded-2xl border bg-white shadow-sm ${boxClass}`}
      >
        <Icon size={24} className={iconClass} />
      </div>
      <div className="mt-1 text-[11px] text-slate-500">{label}</div>
      <Val w={w} />
      {bottom && <div className={`text-[11px] font-semibold ${bottomClass}`}>{bottom}</div>}
    </div>
  );
}

function FlowGraph({ p }) {
  const pv = n(p.pv_power);
  const grid = n(p.grid_power);
  const battery = n(p.battery_power);
  const soc = n(p.battery);
  const ups = n(p.load_ups_power != null ? p.load_ups_power : p.load_power);
  const home = n(p.load_power);
  const onGrid = String(p.device_relay || "").toLowerCase().includes("grid");

  const paths = [
    { id: "fp-pv", d: "M 150 150 H 360 V 415 H 430", active: pv !== 0 },
    { id: "fp-bat", d: "M 150 740 H 360 V 485 H 430", active: battery !== 0 },
    { id: "fp-grid", d: "M 850 150 H 640 V 415 H 570", active: grid !== 0 },
    { id: "fp-home", d: "M 850 740 H 640 V 485 H 570", active: home !== 0 },
    { id: "fp-ups", d: "M 500 520 V 760", active: ups !== 0 },
  ];

  const Arrow = ({ x, y, dir }) => {
    const pts =
      dir === "r"
        ? `${x},${y} ${x - 9},${y - 5} ${x - 9},${y + 5}`
        : `${x},${y} ${x + 9},${y - 5} ${x + 9},${y + 5}`;
    return <polygon points={pts} fill="#3B82F6" />;
  };
  const Dot = ({ x, y }) => (
    <circle cx={x} cy={y} r="5" fill="#fff" stroke="#3B82F6" strokeWidth="2" />
  );

  return (
    <div
      className="relative mx-auto w-full max-w-[440px]"
      style={{ aspectRatio: "1000 / 900" }}
      data-testid="flow-graph"
    >
      <svg viewBox="0 0 1000 900" className="absolute inset-0 h-full w-full">
        {paths.map((pt) => (
          <path
            key={pt.id}
            id={pt.id}
            d={pt.d}
            fill="none"
            stroke="#CBD5E1"
            strokeWidth="2"
            strokeDasharray="7 7"
            strokeLinejoin="round"
          />
        ))}
        {paths
          .filter((pt) => pt.active)
          .map((pt) => (
            <circle key={`${pt.id}-dot`} r="4.5" fill="#3B82F6">
              <animateMotion dur="2.2s" repeatCount="indefinite">
                <mpath href={`#${pt.id}`} />
              </animateMotion>
            </circle>
          ))}

        <Dot x={418} y={415} />
        <Arrow x={432} y={415} dir="r" />
        <Dot x={418} y={485} />
        <Arrow x={432} y={485} dir="r" />
        <Dot x={582} y={415} />
        <Arrow x={568} y={415} dir="l" />
        <Dot x={582} y={485} />
        <Arrow x={568} y={485} dir="l" />
        <Dot x={500} y={650} />

        {onGrid && (
          <g>
            <circle cx="640" cy="300" r="10" fill="#10B981" />
            <path
              d="M 635 300 l 3 3 l 6 -6"
              fill="none"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        )}
      </svg>

      <FGNode x={110} y={150} icon={Sun} iconClass="text-amber-500" boxClass="border-amber-200" label="Сонце (PV)" w={pv} />
      <FGNode
        x={890}
        y={150}
        icon={RadioTower}
        iconClass="text-slate-700"
        label="Мережа"
        w={grid}
        bottom={onGrid ? "В мережі" : null}
        bottomClass="text-emerald-600"
      />
      <FGNode
        x={110}
        y={740}
        icon={BatteryMedium}
        iconClass="text-blue-600"
        boxClass="border-blue-200"
        label="Батарея"
        w={battery}
        top={`${soc}%`}
        topClass="text-blue-600"
      />
      <FGNode x={500} y={800} icon={Monitor} iconClass="text-slate-700" label="UPS" w={ups} />
      <FGNode x={890} y={740} icon={Home} iconClass="text-slate-700" label="Споживання" w={home} />

      <div
        className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center"
        style={{ left: "50%", top: `${(450 / 900) * 100}%` }}
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl border-2 border-blue-500 bg-blue-50 text-blue-600 shadow-sm">
          <Cpu size={24} />
        </div>
        <div className="mt-1 text-[11px] font-medium text-slate-500">Інвертор</div>
      </div>
    </div>
  );
}

const mwh = (kwh) => {
  const v = n(kwh);
  return v >= 1000
    ? { num: (v / 1000).toFixed(2), unit: "MWh" }
    : { num: v.toFixed(2), unit: "kWh" };
};

function SideLabel({ pct, value, name, color, align }) {
  const line = (
    <div className="h-px flex-1" style={{ backgroundColor: color, opacity: 0.5 }} />
  );
  const text = (
    <div className={align === "right" ? "whitespace-nowrap text-right" : "whitespace-nowrap text-left"}>
      <div className="flex items-baseline gap-1.5">
        {align === "right" ? (
          <>
            <span className="text-lg font-bold" style={{ color }}>
              {pct}%
            </span>
            <span className="font-mono text-xs text-slate-400">{value}</span>
          </>
        ) : (
          <>
            <span className="font-mono text-xs text-slate-400">{value}</span>
            <span className="text-lg font-bold" style={{ color }}>
              {pct}%
            </span>
          </>
        )}
      </div>
      <div className="text-xs text-slate-500">{name}</div>
    </div>
  );
  return (
    <div className="flex items-center gap-3">
      {align === "left" && line}
      {text}
      {align === "right" && line}
    </div>
  );
}

function DonutRow({ title, segments }) {
  const total = segments.reduce((s, d) => s + d.value, 0);
  const data = segments.map((s) => ({
    ...s,
    pct: total ? Math.round((s.value / total) * 100) : 0,
  }));
  const left = data.filter((s) => s.side === "l");
  const right = data.filter((s) => s.side === "r");
  const t = mwh(total);
  return (
    <div className="flex items-center gap-4">
      <div className="flex flex-1 flex-col gap-7">
        {left.map((s) => (
          <SideLabel key={s.name} align="right" {...s} />
        ))}
      </div>
      <div className="relative h-36 w-36 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              innerRadius="72%"
              outerRadius="100%"
              startAngle={90}
              endAngle={-270}
              paddingAngle={1}
              stroke="none"
            >
              {data.map((d, i) => (
                <Cell key={i} fill={d.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-sm font-medium text-slate-600">{title}</span>
          <span className="font-mono text-base font-bold text-slate-900">
            {t.num} <span className="text-[10px] text-slate-400">{t.unit}</span>
          </span>
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-7">
        {right.map((s) => (
          <SideLabel key={s.name} align="left" {...s} />
        ))}
      </div>
    </div>
  );
}

const SERIES = [
  { key: "pv", name: "Виробництво", color: "#10B981" },
  { key: "grid", name: "Мережа", color: "#E879F9" },
  { key: "battery", name: "Батарея", color: "#14B8A6" },
  { key: "load", name: "Споживання", color: "#F59E0B" },
];

const GRID_V = [
  { key: "gv1", name: "L1", color: "#F59E0B" },
  { key: "gv2", name: "L2", color: "#10B981" },
  { key: "gv3", name: "L3", color: "#3B82F6" },
];

const HIST_SERIES = [
  { key: "production", name: "Виробництво", color: "#10B981" },
  { key: "discharge", name: "Розряд", color: "#38BDF8" },
  { key: "purchased", name: "Куплено", color: "#8B5CF6" },
  { key: "consumption", name: "Споживання", color: "#F59E0B" },
  { key: "charge", name: "Заряд", color: "#3B82F6" },
  { key: "feed_in", name: "Віддача в мережу", color: "#F472B6" },
];

export default function InverterPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [profile, setProfile] = useState([]);
  const [profileDate, setProfileDate] = useState(new Date());
  const [period, setPeriod] = useState({ mode: "month", date: new Date() });
  const [energy, setEnergy] = useState(null);
  const [histDate, setHistDate] = useState(new Date());
  const [histData, setHistData] = useState([]);

  useEffect(() => {
    const load = () => getDevice(id).then(setDevice).catch(() => {});
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [id]);

  useEffect(() => {
    if (!device) return;
    let active = true;
    const d = profileDate;
    const ds = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
    const load = () =>
      getDeviceProfile(device.id, ds)
        .then((res) => {
          if (!active) return;
          setProfile(
            (res.points || []).map((pt) => ({ ...pt, t: new Date(pt.t).getTime() }))
          );
        })
        .catch(() => active && setProfile([]));
    load();
    const iv = setInterval(load, 30000);
    return () => {
      active = false;
      clearInterval(iv);
    };
  }, [device, profileDate]);

  useEffect(() => {
    if (!device) return;
    const d = period.date;
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
    getDeviceEnergy(device.id, period.mode, dateStr)
      .then(setEnergy)
      .catch(() => setEnergy(null));
  }, [device, period]);

  useEffect(() => {
    if (!device) return;
    const mm = `${histDate.getFullYear()}-${String(histDate.getMonth() + 1).padStart(2, "0")}`;
    getDeviceHistory(device.id, mm)
      .then((res) =>
        setHistData(
          (res.points || []).map((r) => ({
            ...r,
            consumption_n: -r.consumption,
            charge_n: -r.charge,
            feed_n: -r.feed_in,
          }))
        )
      )
      .catch(() => setHistData([]));
  }, [device, histDate]);

  if (!device) {
    return (
      <div className="min-h-screen bg-slate-100 pt-16">
        <TopNav />
        <div className="flex items-center justify-center py-24 text-slate-400">
          <Loader2 className="animate-spin" />
        </div>
      </div>
    );
  }

  const p = device.telemetry?.payload || {};
  const lastOnline = online(device.telemetry?.last_seen);

  const e = energy || {};
  const num1 = (x) => Number(n(x).toFixed(1));
  const consSegments = [
    { name: "Імпорт", value: num1(e.import), color: "#8B5CF6", side: "l" },
    { name: "Розряд", value: num1(e.discharge), color: "#38BDF8", side: "l" },
    { name: "Сонце", value: num1(e.pv), color: "#10B981", side: "r" },
  ];
  const prodSegments = [
    { name: "Заряд", value: num1(e.charge), color: "#3B82F6", side: "l" },
    { name: "Споживання", value: num1(e.consumption), color: "#F59E0B", side: "r" },
  ];

  const histMonthStr = `${histDate.getFullYear()}-${String(histDate.getMonth() + 1).padStart(2, "0")}`;
  const exportHistCsv = () => {
    const header = "day,production,discharge,purchased,consumption,charge,feed_in\n";
    const rows = histData
      .map((r) => `${r.day},${r.production},${r.discharge},${r.purchased},${r.consumption},${r.charge},${r.feed_in}`)
      .join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `generation_usage_${histMonthStr}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const dstr = `${profileDate.getFullYear()}-${String(profileDate.getMonth() + 1).padStart(2, "0")}-${String(profileDate.getDate()).padStart(2, "0")}`;
  const dayStartMs = kyivDayStartMs(profileDate);
  const dayEndMs = dayStartMs + 86400000;
  const dayTicks = kyivTicks(dayStartMs);
  const fmtHour = (t) => fmtKyivHM(t);
  const exportCsv = () => {
    const header = "time,production,grid,battery,consumption,soc\n";
    const rows = profile
      .map((r) => `${new Date(r.t).toISOString()},${r.pv},${r.grid},${r.battery},${r.load},${r.soc}`)
      .join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `power_profile_${dstr}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-100 pt-16">
      <TopNav />
      <div className="mx-auto max-w-[1500px] px-6 py-6">
        <div className="mb-5 flex items-center gap-3">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/devices")}
            className="rounded-lg border border-slate-200 bg-white p-2 text-slate-600 transition-colors hover:bg-slate-50"
          >
            <ArrowLeft size={18} />
          </button>
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
            <Cpu size={22} />
          </span>
          <div>
            <h1 className="font-display text-xl font-extrabold tracking-tight text-slate-900">
              {device.name}
              <span className="ml-2 text-sm font-medium text-slate-400">
                {p.device || "Інвертор"}
              </span>
            </h1>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Circle
                size={8}
                className={lastOnline ? "fill-emerald-500 text-emerald-500" : "fill-slate-300 text-slate-300"}
              />
              {lastOnline ? "В мережі" : "Немає звʼязку"} · {device.telemetry?.last_seen
                ? new Date(device.telemetry.last_seen).toLocaleString()
                : "—"}
            </div>
          </div>
        </div>

        {/* Top blocks */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          <Panel title="Схема потоків" testid="inverter-flow-panel" className="lg:col-span-6">
            <FlowGraph p={p} />
          </Panel>

          <Panel
            testid="inverter-donuts-panel"
            className="lg:col-span-6"
            title={
              <span className="flex items-center gap-1">
                Сонце та споживання <Info size={13} className="text-slate-400" />
              </span>
            }
            right={<PeriodPicker value={period} onChange={setPeriod} />}
          >
            <div className="flex flex-col gap-4 py-2">
              <DonutRow title="Споживання" segments={consSegments} />
              <div className="border-t border-slate-100" />
              <DonutRow title="Виробництво" segments={prodSegments} />
            </div>
          </Panel>
        </div>

        {/* Power profile */}
        <Panel
          title="Профіль потужності"
          testid="inverter-profile-panel"
          className="mt-4"
          right={
            <PeriodPicker
              dayOnly
              value={{ mode: "day", date: profileDate }}
              onChange={(v) => {
                setProfile([]);
                setProfileDate(v.date);
              }}
            />
          }
        >
          <div className="mb-3 flex flex-wrap gap-4">
            {[...SERIES, { key: "soc", name: "SOC", color: "#8B5CF6" }].map((s) => (
              <span key={s.key} className="flex items-center gap-1.5 text-xs text-slate-600">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} />
                {s.name}
              </span>
            ))}
          </div>
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={profile} margin={{ top: 8, right: 8, bottom: 4, left: -12 }}>
                <CartesianGrid stroke="#F1F5F9" vertical={false} />
                <XAxis
                  dataKey="t"
                  type="number"
                  domain={[dayStartMs, dayEndMs]}
                  scale="time"
                  ticks={dayTicks}
                  tickFormatter={fmtHour}
                  stroke="#94A3B8"
                  tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
                />
                <YAxis
                  yAxisId="power"
                  stroke="#94A3B8"
                  tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
                  width={48}
                  label={{ value: "W", position: "insideTopLeft", fontSize: 10, fill: "#94A3B8" }}
                />
                <YAxis
                  yAxisId="soc"
                  orientation="right"
                  domain={[0, 100]}
                  stroke="#94A3B8"
                  tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
                  width={40}
                  label={{ value: "%", position: "insideTopRight", fontSize: 10, fill: "#94A3B8" }}
                />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
                  labelFormatter={(t) => fmtKyivHM(t)}
                />
                {SERIES.map((s) => (
                  <Line
                    key={s.key}
                    yAxisId="power"
                    type="monotone"
                    dataKey={s.key}
                    name={s.name}
                    stroke={s.color}
                    strokeWidth={1.4}
                    dot={false}
                    isAnimationActive={false}
                    connectNulls
                  />
                ))}
                <Line
                  yAxisId="soc"
                  type="monotone"
                  dataKey="soc"
                  name="SOC"
                  stroke="#8B5CF6"
                  strokeWidth={1.4}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        {/* Grid input voltage */}
        <Panel
          title="Вхідна напруга (мережа)"
          testid="inverter-grid-voltage-panel"
          className="mt-4"
          right={
            <PeriodPicker
              dayOnly
              value={{ mode: "day", date: profileDate }}
              onChange={(v) => {
                setProfile([]);
                setProfileDate(v.date);
              }}
            />
          }
        >
          <div className="mb-3 flex flex-wrap gap-4">
            {GRID_V.map((s) => (
              <span key={s.key} className="flex items-center gap-1.5 text-xs text-slate-600">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} />
                {s.name}
              </span>
            ))}
          </div>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={profile} margin={{ top: 8, right: 8, bottom: 4, left: -8 }}>
                <CartesianGrid stroke="#F1F5F9" vertical={false} />
                <XAxis
                  dataKey="t"
                  type="number"
                  domain={[dayStartMs, dayEndMs]}
                  scale="time"
                  ticks={dayTicks}
                  tickFormatter={fmtHour}
                  stroke="#94A3B8"
                  tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
                />
                <YAxis
                  domain={["auto", "auto"]}
                  stroke="#94A3B8"
                  tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
                  width={48}
                  label={{ value: "В", position: "insideTopLeft", fontSize: 10, fill: "#94A3B8" }}
                />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
                  labelFormatter={(t) => fmtKyivHM(t)}
                  formatter={(v, name) => [`${v} В`, name]}
                />
                {GRID_V.map((s) => (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.name}
                    stroke={s.color}
                    strokeWidth={1.4}
                    dot={false}
                    isAnimationActive={false}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        {/* History */}
        <Panel
          title="Історія генерації та споживання"
          testid="inverter-history-panel"
          className="mt-4"
          right={
            <PeriodPicker
              hideToggle
              value={{ mode: "month", date: histDate }}
              onChange={(v) => setHistDate(v.date)}
            />
          }
        >
          <div className="mb-3 flex flex-wrap gap-4">
            {HIST_SERIES.map((s) => (
              <span key={s.key} className="flex items-center gap-1.5 text-xs text-slate-600">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} />
                {s.name}
              </span>
            ))}
          </div>
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={histData} margin={{ top: 8, right: 8, bottom: 4, left: -6 }} stackOffset="sign">
                <CartesianGrid stroke="#F1F5F9" vertical={false} />
                <XAxis
                  dataKey="day"
                  stroke="#94A3B8"
                  tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
                  interval={0}
                />
                <YAxis
                  stroke="#94A3B8"
                  tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
                  width={52}
                  label={{ value: "kWh", position: "insideTopLeft", fontSize: 10, fill: "#94A3B8" }}
                />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
                  labelFormatter={(d) => `День ${d}`}
                  formatter={(v, name) => [`${Math.abs(v)} kWh`, name]}
                />
                <Bar dataKey="production" name="Виробництво" stackId="a" fill="#10B981" />
                <Bar dataKey="discharge" name="Розряд" stackId="a" fill="#38BDF8" />
                <Bar dataKey="purchased" name="Куплено" stackId="a" fill="#8B5CF6" />
                <Bar dataKey="consumption_n" name="Споживання" stackId="a" fill="#F59E0B" />
                <Bar dataKey="charge_n" name="Заряд" stackId="a" fill="#3B82F6" />
                <Bar dataKey="feed_n" name="Віддача в мережу" stackId="a" fill="#F472B6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>
    </div>
  );
}
